import { useLazyQuery } from '@apollo/client';
import dayjs from 'dayjs';
import { Button, Icons } from 'plume-ui';
import { useEffect, useMemo, useRef, useState } from 'react';
import { CSVLink } from 'react-csv';
import { useTranslation } from 'react-i18next';

import { Maybe } from '../../../../../types';
import { getUnresolvedLocationsMonitor } from '../../../services/monitorQueries';
import { Monitor } from '../../../types';
import { rawUnresolvedLocationsToCSV } from '../helpers';

interface DownloadUnresolvedListsButtonProps {
  monitor?: Monitor;
  locationCount?: number;
  monitorName?: string;
  label?: string;
  timestamp?: string;
  disabled?: boolean;
  isDaily?: boolean;
}

const DownloadUnresolvedListsButton = ({
  monitor,
  locationCount,
  timestamp,
  label,
  disabled,
  isDaily,
}: DownloadUnresolvedListsButtonProps) => {
  const { t } = useTranslation();

  // useLazyQuery has a loading prop but returns false after the first call
  const [loading, setLoading] = useState(false);
  const [lastTimeStampDownloaded, setLastTimeStampDownloaded] = useState<
    Maybe<string>
  >(undefined);

  const csvLinkRef = useRef<
    CSVLink & HTMLAnchorElement & { link: HTMLAnchorElement }
  >(null);

  const [getUnresolvedLocations, { data }] = useLazyQuery(
    timestamp
      ? getUnresolvedLocationsMonitor({
        monitorId: monitor?.id || '',
        limit: locationCount || 0,
        offset: 0,
        isDaily: isDaily,
        timestamp,
      })
      : getUnresolvedLocationsMonitor({
        monitorId: monitor?.id || '',
        limit: locationCount || 0,
        offset: 0,
      }),
    {
      fetchPolicy: 'no-cache',
    },
  );

  const csvData = useMemo(
    () =>
      data && !loading && timestamp === lastTimeStampDownloaded
        ? rawUnresolvedLocationsToCSV(
          data?.signal_3_p_evaluate_monitor_freq,
          !Boolean(timestamp),
        )
        : [],
    [data, loading, timestamp],
  );

  const handleDownload = async () => {
    setLoading(true);
    await getUnresolvedLocations();
    setLastTimeStampDownloaded(timestamp);
    setLoading(false);
  };

  useEffect(() => {
    if (csvData.length > 0 && csvLinkRef.current && !loading) {
      // this is a hack to trigger the download at the right time
      // but only because CSVDownload does not support the filename prop
      csvLinkRef.current.link.click();
    }
  }, [csvData, loading]);

  return (
    <div className="MonitorInfo__unresolvedCsvButton">
      <Button
        styleVariant="tertiary"
        onClick={handleDownload}
        disabled={disabled}
        loading={loading}
        icon={<Icons.DownloadIcon />}
      >
        {label || t('download')}
      </Button>

      <CSVLink
        ref={csvLinkRef}
        filename={`${monitor?.name}-unresolved-list-${timestamp
          ? dayjs(timestamp).format('YYYY-MM-DDTHHmm')
          : dayjs().format('YYYY-MM-DDTHHmmss')
          }.csv`}
        data={csvData}
      />
    </div>
  );
};

export default DownloadUnresolvedListsButton;
